import React, { useState, useCallback, useEffect } from "react";
import Header from "../components/Header";
import { API_BASE_URL, API_ENDPOINTS } from "../constants/config";
import { stockService } from "../services/apiService";
import { useProfile } from "../utils/UseProfile";
import Toast from "../components/Toast";
import * as XLSX from "xlsx";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";

// ─── Helpers ──────────────────────────────────────────────────────────────────
const fmtDate = (d) => {
  if (!d) return "—";
  const dt = new Date(d);
  return `${String(dt.getDate()).padStart(2, "0")}/${String(dt.getMonth() + 1).padStart(2, "0")}/${dt.getFullYear()}`;
};

const toInputDate = (d) => {
  if (!d) return "";
  const dt = new Date(d);
  return `${dt.getFullYear()}-${String(dt.getMonth() + 1).padStart(2, "0")}-${String(dt.getDate()).padStart(2, "0")}`;
};

const isExpiringSoon = (expiryDate) => {
  if (!expiryDate) return false;
  const diff = new Date(expiryDate) - new Date();
  return diff > 0 && diff < 90 * 24 * 60 * 60 * 1000; // within 90 days
};

const isExpired = (expiryDate) =>
  expiryDate && new Date(expiryDate) < new Date();

// ─── Remarks builder ─────────────────────────────────────────────────────────
const buildRemarks = (h) => {
  if (h.remarks) return h.remarks;
  const prev = h.previousValues || h.prevValues || {};
  const next = h.newValues || h.updatedValues || {};
  const labels = { quantity: "Qty", mrp: "MRP", purchasePrice: "Purchase₹", salesDiscount: "Disc%", batchNo: "Batch", size: "Size", expiryDate: "Expiry", purchaseDate: "PurchDate" };
  const parts = Object.keys(labels)
    .filter((k) => next[k] !== undefined && String(prev[k] ?? "") !== String(next[k] ?? ""))
    .map((k) => `${labels[k]}: ${prev[k] ?? "—"} → ${next[k]}`);
  if (!parts.length && h.quantityChanged != null) {
    const sign = h.quantityChanged > 0 ? "+" : "";
    return `Qty ${sign}${h.quantityChanged} → ${h.remainingQty}`;
  }
  return parts.join(" | ") || "—";
};

// ─── History Modal ────────────────────────────────────────────────────────────
const HistoryModal = ({ stockId, productName, onClose, showSize }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);

  React.useEffect(() => {
    stockService
      .getStockHistory(stockId)
      .then(setHistory)
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, [stockId]);

  const badgeColor = {
    ADD: "bg-green-100 text-green-700",
    UPDATE: "bg-blue-100 text-blue-700",
    SALE: "bg-orange-100 text-orange-700",
    RETURN: "bg-purple-100 text-purple-700",
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[98vw] max-h-[85vh] flex flex-col">
        <div className="flex items-center justify-between px-5 py-4 border-b border-gray-200">
          <h3 className="font-bold text-blue-800 text-lg">
            <i className="fas fa-history mr-2 text-indigo-500" />
            History — {productName}
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl">
            <i className="fas fa-times" />
          </button>
        </div>
        <div className="overflow-auto flex-1 p-4">
          {loading ? (
            <div className="text-center py-8">
              <i className="fas fa-spinner fa-spin text-blue-400 text-2xl" />
            </div>
          ) : history.length === 0 ? (
            <p className="text-center text-gray-400 py-8">No history found</p>
          ) : (
            <table className="text-sm" style={{ minWidth: "900px", width: "100%" }}>
              <thead className="bg-gray-50 text-gray-600 uppercase text-xs">
                <tr>
                  <th className="py-2 px-3 text-left whitespace-nowrap">Type</th>
                  <th className="py-2 px-3 text-left whitespace-nowrap">Batch No</th>
                  {showSize && <th className="py-2 px-3 text-left whitespace-nowrap">Size</th>}
                  <th className="py-2 px-3 text-right whitespace-nowrap">Qty Changed</th>
                  <th className="py-2 px-3 text-right whitespace-nowrap">Remaining</th>
                  <th className="py-2 px-3 text-left whitespace-nowrap">Changed By</th>
                  <th className="py-2 px-3 text-left whitespace-nowrap">Date</th>
                  <th className="py-2 px-3 text-left" style={{ minWidth: "260px" }}>Remarks</th>
                </tr>
              </thead>
              <tbody>
                {history.map((h) => (
                  <tr key={h.historyId} className="border-t hover:bg-gray-50">
                    <td className="py-2 px-3 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${badgeColor[h.changeType] || "bg-gray-100 text-gray-600"}`}>
                        {h.changeType}
                      </span>
                    </td>
                    <td className="py-2 px-3 text-xs text-gray-600 whitespace-nowrap">{h.batchNo || "—"}</td>
                    {showSize && <td className="py-2 px-3 text-xs text-gray-600 whitespace-nowrap">{h.size ?? "—"}</td>}
                    <td className={`py-2 px-3 text-right font-mono font-semibold whitespace-nowrap ${h.quantityChanged < 0 ? "text-red-600" : "text-green-600"}`}>
                      {h.quantityChanged > 0 ? `+${h.quantityChanged}` : h.quantityChanged}
                    </td>
                    <td className="py-2 px-3 text-right font-mono whitespace-nowrap">{h.remainingQty}</td>
                    <td className="py-2 px-3 text-gray-600 whitespace-nowrap">{h.changedBy || "—"}</td>
                    <td className="py-2 px-3 text-gray-500 text-xs whitespace-nowrap">
                      {h.changedOn ? new Date(h.changedOn).toLocaleString() : "—"}
                    </td>
                    <td className="py-2 px-3 text-xs text-indigo-700 whitespace-nowrap">{buildRemarks(h)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
};


// ─── Full Stock History Modal ─────────────────────────────────────────────────
const HIST_PAGE_SIZE = 20;

const FullHistoryModal = ({ onClose, showSize }) => {
  const [history, setHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("");
  const [filterStockId, setFilterStockId] = useState("");
  const [filterBatchNo, setFilterBatchNo] = useState("");
  const [filterSize, setFilterSize] = useState("");
  const [page, setPage] = useState(0);
  const [sortConfig, setSortConfig] = useState({ key: "changedOn", dir: "desc" });
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const [dragging, setDragging] = useState(false);
  const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

  const handleMouseDown = (e) => {
    setDragging(true);
    setDragStart({ x: e.clientX - pos.x, y: e.clientY - pos.y });
  };

  const handleMouseMove = (e) => {
    if (!dragging) return;
    setPos({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
  };

  const handleMouseUp = () => setDragging(false);

  React.useEffect(() => {
    stockService.getAllStockHistory()
      .then(setHistory)
      .catch(() => setHistory([]))
      .finally(() => setLoading(false));
  }, []);

  const badgeColor = {
    ADD: "bg-green-100 text-green-700",
    UPDATE: "bg-blue-100 text-blue-700",
    SALE: "bg-orange-100 text-orange-700",
    RETURN: "bg-purple-100 text-purple-700",
  };

  const filtered = history.filter((h) => {
    if (filter.trim()) {
      const q = filter.toLowerCase();
      const match = (h.productName || "").toLowerCase().includes(q) ||
        (h.changeType || "").toLowerCase().includes(q) ||
        (h.changedBy || "").toLowerCase().includes(q) ||
        (h.barcodeSource || "").toLowerCase().includes(q) ||
        (h.batchNo || "").toLowerCase().includes(q) ||
        String(h.historyId || "").includes(q) ||
        String(h.stockId || "").includes(q) ||
        String(h.productId || "").includes(q);
      if (!match) return false;
    }
    if (filterStockId.trim() && !String(h.stockId || "").includes(filterStockId.trim())) return false;
    if (filterBatchNo.trim() && !(h.batchNo || "").toLowerCase().includes(filterBatchNo.trim().toLowerCase())) return false;
    if (filterSize.trim() && !String(h.size ?? "").includes(filterSize.trim())) return false;
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    const key = sortConfig.key;
    let aVal = a[key] ?? "";
    let bVal = b[key] ?? "";
    if (key === "changedOn") { aVal = new Date(aVal || 0); bVal = new Date(bVal || 0); }
    if (typeof aVal === "number" && typeof bVal === "number") {
      return sortConfig.dir === "asc" ? aVal - bVal : bVal - aVal;
    }
    const cmp = String(aVal).localeCompare(String(bVal));
    return sortConfig.dir === "asc" ? cmp : -cmp;
  });

  const totalPages = Math.ceil(sorted.length / HIST_PAGE_SIZE);
  const paged = sorted.slice(page * HIST_PAGE_SIZE, (page + 1) * HIST_PAGE_SIZE);

  React.useEffect(() => { setPage(0); }, [filter, filterStockId, filterBatchNo, filterSize]);

  const handleSort = (key) => {
    setSortConfig((prev) => ({ key, dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc" }));
  };

  const SortTh = ({ col, label, className = "text-left" }) => (
    <th className={`py-2 px-3 ${className} cursor-pointer select-none hover:bg-blue-100`} onClick={() => handleSort(col)}>
      {label}
      {sortConfig.key === col
        ? <i className={`fas fa-sort-${sortConfig.dir === "asc" ? "up" : "down"} text-indigo-500 ml-1 text-xs`} />
        : <i className="fas fa-sort text-gray-300 ml-1 text-xs" />}
    </th>
  );

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50 p-4" onClick={onClose} onMouseMove={handleMouseMove} onMouseUp={handleMouseUp}>
      <div className="bg-white rounded-xl shadow-2xl w-full max-w-[98vw] max-h-[90vh] flex flex-col" onClick={(e) => e.stopPropagation()} style={{ transform: `translate(${pos.x}px, ${pos.y}px)` }}>
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b border-gray-200 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 rounded-t-xl cursor-move select-none" onMouseDown={handleMouseDown}>
          <div className="flex items-center gap-3">
            <h3 className="font-bold text-blue-800 text-lg">
              <i className="fas fa-history mr-2 text-indigo-500" />
              Stock History
            </h3>
            {sorted.length > 0 && <span className="text-xs text-blue-600">({sorted.length})</span>}
          </div>
          <div className="flex items-center gap-2 flex-wrap">
            <input
              type="text"
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder="Filter by product, type, user..."
              className="w-48 px-3 py-1.5 border border-blue-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white"
            />
            <input
              type="text"
              value={filterStockId}
              onChange={(e) => setFilterStockId(e.target.value.replace(/\D/g, ""))}
              placeholder="Stock ID e.g. 101"
              className="w-28 px-2 py-1.5 border border-blue-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white"
            />
            <input
              type="text"
              value={filterBatchNo}
              onChange={(e) => setFilterBatchNo(e.target.value)}
              placeholder="Batch No e.g. B001"
              className="w-32 px-2 py-1.5 border border-blue-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white"
            />
            {showSize && (
              <input
                type="text"
                value={filterSize}
                onChange={(e) => setFilterSize(e.target.value)}
                placeholder="Size e.g. 7"
                className="w-24 px-2 py-1.5 border border-blue-300 rounded-lg text-xs focus:ring-2 focus:ring-blue-400 focus:outline-none bg-white"
              />
            )}
            {(filter || filterStockId || filterBatchNo || filterSize) && (
              <button
                onClick={() => { setFilter(""); setFilterStockId(""); setFilterBatchNo(""); setFilterSize(""); }}
                className="text-xs text-gray-400 hover:text-red-500 transition px-1"
                title="Clear filters"
              >
                <i className="fas fa-times-circle" />
              </button>
            )}
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 text-xl ml-1">
              <i className="fas fa-times" />
            </button>
          </div>
        </div>

        {/* Table */}
        <div className="overflow-auto flex-1">
          <table className="text-sm" style={{ minWidth: "1400px", width: "100%" }}>
            <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 text-xs uppercase tracking-wide sticky top-0 z-10">
              <tr>
                <SortTh col="historyId" label="ID" />
                <SortTh col="stockId" label="Stock ID" />
                <SortTh col="productId" label="Product ID" />
                <SortTh col="productName" label="Product" />
                <SortTh col="batchNo" label="Batch No" />
                {showSize && <SortTh col="size" label="Size" />}
                <SortTh col="changeType" label="Type" />
                <SortTh col="quantityChanged" label="Qty Changed" className="text-right" />
                <SortTh col="remainingQty" label="Remaining" className="text-right" />
                <SortTh col="changedBy" label="Changed By" />
                <SortTh col="barcodeSource" label="Barcode Source" />
                <SortTh col="changedOn" label="Date" />
                <th className="py-2 px-3 text-left whitespace-nowrap" style={{ minWidth: "280px" }}>Remarks</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={13} className="py-10 text-center text-blue-400">
                  <i className="fas fa-spinner fa-spin text-2xl" />
                </td></tr>
              ) : paged.length === 0 ? (
                <tr><td colSpan={13} className="py-10 text-center text-gray-400">
                  <i className="fas fa-history text-4xl mb-3 block" />
                  <p className="text-sm">{filter ? "No history matches filter." : "No history found."}</p>
                </td></tr>
              ) : (
                paged.map((h, idx) => (
                  <tr key={h.historyId || idx} className={`border-t hover:bg-indigo-50 ${idx % 2 === 0 ? "bg-white" : "bg-gray-50"}`}>
                    <td className="py-2 px-3 text-xs text-gray-500 whitespace-nowrap">{h.historyId}</td>
                    <td className="py-2 px-3 text-xs text-gray-500 whitespace-nowrap">{h.stockId}</td>
                    <td className="py-2 px-3 text-xs text-gray-500 whitespace-nowrap">{h.productId}</td>
                    <td className="py-2 px-3 font-medium whitespace-nowrap">{h.productName || "-"}</td>
                    <td className="py-2 px-3 text-xs text-gray-600 whitespace-nowrap">{h.batchNo || "-"}</td>
                    {showSize && <td className="py-2 px-3 text-xs text-gray-600 whitespace-nowrap">{h.size ?? "-"}</td>}
                    <td className="py-2 px-3 whitespace-nowrap">
                      <span className={`px-2 py-0.5 rounded-full text-xs font-semibold ${badgeColor[h.changeType] || "bg-gray-100 text-gray-600"}`}>
                        {h.changeType}
                      </span>
                    </td>
                    <td className={`py-2 px-3 text-right font-mono font-semibold whitespace-nowrap ${h.quantityChanged < 0 ? "text-red-600" : "text-green-600"}`}>
                      {h.quantityChanged > 0 ? `+${h.quantityChanged}` : h.quantityChanged}
                    </td>
                    <td className="py-2 px-3 text-right font-mono whitespace-nowrap">{h.remainingQty}</td>
                    <td className="py-2 px-3 text-gray-600 whitespace-nowrap">{h.changedBy || "-"}</td>
                    <td className="py-2 px-3 text-xs text-gray-500 whitespace-nowrap">{h.barcodeSource || "-"}</td>
                    <td className="py-2 px-3 text-gray-500 text-xs whitespace-nowrap">
                      {h.changedOn ? new Date(h.changedOn).toLocaleString() : "-"}
                    </td>
                    <td className="py-2 px-3 text-xs text-indigo-700 whitespace-nowrap">{buildRemarks(h)}</td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>

        {/* Pagination */}
        {sorted.length > HIST_PAGE_SIZE && (
          <div className="flex items-center justify-between px-5 py-2 border-t border-gray-100 bg-white flex-shrink-0 rounded-b-xl">
            <span className="text-xs text-gray-500">
              Showing {page * HIST_PAGE_SIZE + 1}–{Math.min((page + 1) * HIST_PAGE_SIZE, sorted.length)} of {sorted.length}
            </span>
            <div className="flex items-center gap-1">
              <button onClick={() => setPage(0)} disabled={page === 0} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">«</button>
              <button onClick={() => setPage((p) => p - 1)} disabled={page === 0} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">‹</button>
              {Array.from({ length: totalPages }, (_, i) => (
                <button key={i} onClick={() => setPage(i)}
                  className={`px-2 py-1 text-xs rounded border ${i === page ? "bg-indigo-500 text-white border-indigo-500" : "border-gray-200 hover:bg-gray-50"}`}>{i + 1}</button>
              )).slice(Math.max(0, page - 2), page + 3)}
              <button onClick={() => setPage(page + 1)} disabled={page >= totalPages - 1} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">›</button>
              <button onClick={() => setPage(totalPages - 1)} disabled={page >= totalPages - 1} className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50">»</button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

// ─── Inline editable cell ─────────────────────────────────────────────────────
const EditCell = ({
  editing,
  type = "text",
  name,
  value,
  onChange,
  step,
  min,
  placeholder,
  display,
  inputClassName = "",
}) => {
  if (!editing) return <span>{display ?? value ?? "—"}</span>;
  return (
    <input
      type={type}
      name={name}
      value={value}
      onChange={onChange}
      step={step}
      min={min}
      placeholder={placeholder}
      className={`px-2 py-1 border border-indigo-300 rounded text-sm focus:ring-1 focus:ring-indigo-400 focus:outline-none ${inputClassName || "w-full min-w-[80px]"}`}
    />
  );
};

// ─── Sort helpers (outside component to avoid jsx-no-undef) ─────────────────
const SortIcon = ({ col, sortConfig }) => {
  if (!sortConfig || sortConfig.key !== col)
    return <i className="fas fa-sort text-gray-300 ml-1 text-xs" />;
  return sortConfig.dir === "asc" ? (
    <i className="fas fa-sort-up text-indigo-500 ml-1 text-xs" />
  ) : (
    <i className="fas fa-sort-down text-indigo-500 ml-1 text-xs" />
  );
};

const Th = ({ col, label, className = "text-left", sortConfig, onSort }) => (
  <th
    className={`py-2 px-4 ${className} cursor-pointer select-none hover:bg-blue-100`}
    onClick={() => onSort(col)}
  >
    {label}
    <SortIcon col={col} sortConfig={sortConfig} />
  </th>
);

// ─── Main component ───────────────────────────────────────────────────────────
const StockSearch = () => {
  const { profile } = useProfile();
  const isAdmin = profile?.roleResponse?.role?.toLowerCase().includes("admin");

  const SS = "stock_search";
  const ss = () => {
    try {
      return JSON.parse(sessionStorage.getItem(SS)) || {};
    } catch {
      return {};
    }
  };
  const saveSession = (patch) => {
    const token = localStorage.getItem("token") || "";
    try {
      sessionStorage.setItem(
        SS,
        JSON.stringify({ ...ss(), _token: token, ...patch }),
      );
    } catch {}
  };

  const currentToken = localStorage.getItem("token") || "";
  const savedSession = ss();
  const sessionValid = savedSession._token === currentToken;

  const [searchName, setSearchName] = useState(() =>
    sessionValid ? savedSession.searchName || "" : "",
  );
  const [searchMfr, setSearchMfr] = useState(() =>
    sessionValid ? savedSession.searchMfr || "" : "",
  );
  const [searchBillNo, setSearchBillNo] = useState(() =>
    sessionValid ? savedSession.searchBillNo || "" : "",
  );
  const [searchDistName, setSearchDistName] = useState(() =>
    sessionValid ? savedSession.searchDistName || "" : "",
  );
  const [searchDistMobile, setSearchDistMobile] = useState(() =>
    sessionValid ? savedSession.searchDistMobile || "" : "",
  );
  const [results, setResults] = useState([]);
  const [searched, setSearched] = useState(false);
  const [page, setPage] = useState(() =>
    sessionValid ? savedSession.page || 0 : 0,
  );
  const [loading, setLoading] = useState(false);
  const [toast, setToast] = useState(null);
  const PAGE_SIZE = 20;
  const [totalElements, setTotalElements] = useState(0);

  const [editMap, setEditMap] = useState({});
  const [savingId, setSavingId] = useState(null);
  const [deletingId, setDeletingId] = useState(null);
  const [historyTarget, setHistoryTarget] = useState(null);
  const [showFullHistory, setShowFullHistory] = useState(false);
  const [clientFilter, setClientFilter] = useState("");
  const [batchFilter, setBatchFilter] = useState("");

  // Auto-load controlled by per-client setting (default: true)
  useEffect(() => {
    stockService.getStockSearchDefaultLoad()
      .then((res) => {
        if (res?.value === false) {
          // Setting is OFF — clear any stale session so nothing shows
          sessionStorage.removeItem(SS);
        } else {
          handleSearch();
        }
      })
      .catch(() => handleSearch()); // fail-safe: load on error
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    stockService
      .getClientFieldConfig()
      .then((res) => {
        const configMap = {};

        (res || []).forEach((f) => {
          configMap[f.fieldName] = f.visible;
        });

        const normalizeConfig = (config) => {
          const result = {};

          Object.keys(config || {}).forEach((key) => {
            const val = config[key];

            if (typeof val === "boolean") {
              result[key] = { visible: val, mandatory: false };
            } else {
              result[key] = {
                visible: !!val?.visible,
                mandatory: !!val?.mandatory,
              };
            }
          });

          return result;
        };

        setFieldConfig(normalizeConfig(configMap));
      })
      .catch(() => setFieldConfig({}));
  }, []);

  const showMsg = (type, text, identifier = null) => {
    setToast({ type, identifier, message: text.replace(/^[✅⚠️❌]\s*/, "") });
  };

  const downloadPDF = async () => {
    try {
      const params = new URLSearchParams();
      if (searchName.trim()) params.append("name", searchName.trim());
      if (searchMfr.trim()) params.append("manufacturer", searchMfr.trim());
      if (searchBillNo.trim()) params.append("billNo", searchBillNo.trim());
      if (searchDistName.trim())
        params.append("distributorName", searchDistName.trim());
      if (searchDistMobile.trim())
        params.append("distributorMobile", searchDistMobile.trim());
      const url = `${API_BASE_URL}${API_ENDPOINTS.STOCK_REPORT_PDF}?${params.toString()}`;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "X-Authenticated-User": localStorage.getItem("userName") || "",
          Accept: "application/pdf",
        },
      });
      if (!res.ok) throw new Error("Failed to generate PDF");
      const contentType = res.headers.get("content-type") || "";
      if (!contentType.includes("pdf")) throw new Error("Invalid response - not a PDF");
      const blob = await res.blob();
      const pdfBlob = new Blob([blob], { type: "application/pdf" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(pdfBlob);
      link.download = `stock_report_${new Date().toISOString().slice(0, 10)}.pdf`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      showMsg("error", "Failed to download PDF");
    }
  };

  const downloadExcel = async () => {
    try {
      const params = new URLSearchParams();
      if (searchName.trim()) params.append("name", searchName.trim());
      if (searchMfr.trim()) params.append("manufacturer", searchMfr.trim());
      if (searchBillNo.trim()) params.append("billNo", searchBillNo.trim());
      if (searchDistName.trim())
        params.append("distributorName", searchDistName.trim());
      if (searchDistMobile.trim())
        params.append("distributorMobile", searchDistMobile.trim());
      const url = `${API_BASE_URL}${API_ENDPOINTS.STOCK_REPORT_EXCEL}?${params.toString()}`;
      const res = await fetch(url, {
        headers: {
          Authorization: `Bearer ${localStorage.getItem("token")}`,
          "X-Authenticated-User": localStorage.getItem("userName") || "",
        },
      });
      if (!res.ok) throw new Error("Failed to generate Excel");
      const blob = await res.blob();
      const excelBlob = new Blob([blob], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(excelBlob);
      link.download = `stock_report_${new Date().toISOString().slice(0, 10)}.xlsx`;
      link.click();
      URL.revokeObjectURL(link.href);
    } catch (err) {
      showMsg("error", "Failed to download Excel");
    }
  };

  const [sortConfig, setSortConfig] = useState({ key: null, dir: "asc" });
  const [fieldConfig, setFieldConfig] = useState({});
  const handleSort = (key) => {
    setSortConfig((prev) => ({
      key,
      dir: prev.key === key && prev.dir === "asc" ? "desc" : "asc",
    }));
  };

  const baseCols = 10;

  const optionalCols =
    (fieldConfig?.expiryDate?.visible ? 1 : 0) + (fieldConfig?.size?.visible? 1 : 0) + (fieldConfig?.boxNo?.visible ? 1 : 0);

  const totalCols = baseCols + optionalCols + (isAdmin ? 1 : 0);

  // Client-side filter on product name
  const filteredResults = results.filter((r) => {
    if (clientFilter.trim() && !r.productName?.toLowerCase().includes(clientFilter.trim().toLowerCase())) return false;
    if (batchFilter.trim() && !r.batchNo?.toLowerCase().includes(batchFilter.trim().toLowerCase())) return false;
    return true;
  });

  const sortedResults = [...filteredResults].sort((a, b) => {
    if (!sortConfig.key) return 0;
    const getVal = (r) => {
      if (sortConfig.key === "distributorName")
        return r.distributorInfo?.distributorName || "";
      if (sortConfig.key === "billNumber")
        return r.distributorInfo?.billNumber || "";
      return r[sortConfig.key] ?? "";
    };
    const aVal = getVal(a);
    const bVal = getVal(b);
    const cmp =
      typeof aVal === "number" && typeof bVal === "number"
        ? aVal - bVal
        : String(aVal).localeCompare(String(bVal));
    return sortConfig.dir === "asc" ? cmp : -cmp;
  });

  const getErrMsg = (err, fallback) =>
    err.response?.data?.errorMessage ||
    err.response?.data?.message ||
    err.response?.data?.error ||
    (typeof err.response?.data === "string" ? err.response.data : null) ||
    fallback;

  // ─── Search ────────────────────────────────────────────────────────────────
  const handleSearch = useCallback(async () => {
    setLoading(true);
    setSearched(true);
    saveSession({
      searchName,
      searchMfr,
      searchBillNo,
      searchDistName,
      searchDistMobile,
      searched: true,
    });

    try {
      let allResults = [];
      let pg = 0;
      let hasMore = true;
      while (hasMore) {
        const data = await stockService.searchStock(
          searchName.trim(),
          searchMfr.trim(),
          searchBillNo.trim(),
          searchDistName.trim(),
          searchDistMobile.trim(),
          pg,
          500,
        );
        const chunk = Array.isArray(data) ? data : Array.isArray(data.content) ? data.content : [];
        allResults = [...allResults, ...chunk];
        hasMore = !data.last && chunk.length === 500;
        pg++;
      }
      const list = allResults;
      setResults(list);
      setTotalElements(list.length);
      setPage(0);
      saveSession({ results: list, page: 0 });
      if (!list.length)
        showMsg("error", "No stock found for the given criteria");
    } catch (err) {
      showMsg("error", getErrMsg(err, "No stock found for the given criteria"));
      setResults([]);
      setTotalElements(0);
      saveSession({ results: [] });
    } finally {
      setLoading(false);
    }
  }, [searchName, searchMfr, searchBillNo, searchDistName, searchDistMobile]);



  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  // AI "open this page" handoff: prefill the stock filters and run the existing
  // search once. Stock Search has no customer-mobile filter (distributor only).
  const [navStockSearch, setNavStockSearch] = useState(false);
  useEffect(() => {
    let nav = null;
    try {
      const raw = sessionStorage.getItem("jashNav");
      if (raw) {
        const parsed = JSON.parse(raw);
        if (parsed && parsed.page === "stock_search") nav = parsed;
      }
    } catch { nav = null; }
    if (!nav) return;
    try { sessionStorage.removeItem("jashNav"); } catch { /* noop */ }
    const p = nav.prefill || {};
    if (p.name) setSearchName(p.name);
    if (p.manufacturer) setSearchMfr(p.manufacturer);
    if (p.billNo) setSearchBillNo(p.billNo);
    if (p.distributorName) setSearchDistName(p.distributorName);
    if (p.distributorMobile) setSearchDistMobile(p.distributorMobile);
    if (nav.autoSearch !== false) setNavStockSearch(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (!navStockSearch) return;
    setNavStockSearch(false);
    handleSearch();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navStockSearch, searchName, searchMfr, searchBillNo, searchDistName, searchDistMobile]);


  // ─── Inline edit ──────────────────────────────────────────────────────────
  const startEdit = (stock) => {
    setEditMap((prev) => ({
      ...prev,
      [stock.stockId]: {
        batchNo: stock.batchNo || "",
        boxNo: stock.boxNo || "",
        medicineType: stock.medicineType??"",
        quantity: stock.quantity ?? "",
        stripCount: stock.stripCount ?? "",
        qtyPerStrip: stock.qtyPerStrip ?? "",
        purchasePrice: stock.purchasePrice ?? "",
        mrp: stock.mrp ?? "",
        salesDiscount: stock.salesDiscount ?? "",
        size: stock.size?? "",
        purchaseDate: toInputDate(stock.purchaseDate),
        expiryDate: toInputDate(stock.expiryDate),
      },
    }));
  };

  const cancelEdit = (stockId) => {
    setEditMap((prev) => {
      const n = { ...prev };
      delete n[stockId];
      return n;
    });
  };

  const handleEditChange = (stockId, e) => {
    const { name, value } = e.target;
    setEditMap((prev) => ({
      ...prev,
      [stockId]: { ...prev[stockId], [name]: value },
    }));
  };

  const handleSave = async (stockId) => {
    const data = editMap[stockId];
    setSavingId(stockId);
    try {
      await stockService.updateStock([
        {
          stockId,
          productId: results.find((r) => r.stockId === stockId)?.productId,
          medicineType: data.medicineType || null,
          batchNo: data.batchNo || null,
          boxNo: data.boxNo || null,
          quantity: parseInt(data.quantity),
          stripCount: data.stripCount ? parseInt(data.stripCount) : null,
          qtyPerStrip: data.qtyPerStrip ? parseInt(data.qtyPerStrip) : null,
          purchasePrice: parseFloat(data.purchasePrice),
          mrp: parseFloat(data.mrp),
          salesDiscount: data.salesDiscount
            ? parseFloat(data.salesDiscount)
            : 0,
          size: data.size
            ? parseFloat(data.size)
            : 0,  
          purchaseDate: data.purchaseDate || null,
          expiryDate: data.expiryDate || null,
          actionBy: localStorage.getItem("userName") || "system",
        },
      ]);

      // Update local results
      setResults((prev) =>
        prev.map((r) =>
          r.stockId === stockId
            ? { ...r, ...data, quantity: parseInt(data.quantity) }
            : r,
        ),
      );
      cancelEdit(stockId);
      showMsg(
        "success",
        "Stock batch updated successfully",
        `Product: ${results.find((r) => r.stockId === stockId)?.productName}`,
      );
    } catch (err) {
      showMsg(
        "error",
        err.response?.data?.errorMessage || "Update failed",
        `Product: ${results.find((r) => r.stockId === stockId)?.productName}`,
      );
    } finally {
      setSavingId(null);
    }
  };

  // ─── Delete ────────────────────────────────────────────────────────────────
  const handleDelete = async (stockId, productName) => {
    if (!window.confirm(`Delete stock batch for "${productName}"?`)) return;
    setDeletingId(stockId);
    try {
      await stockService.deleteStock(stockId);
      setResults((prev) => prev.filter((r) => r.stockId !== stockId));
      showMsg("success", "Stock batch deleted", `Product: ${productName}`);
    } catch (err) {
      showMsg(
        "error",
        err.response?.data?.errorMessage || "Delete failed",
        `Product: ${productName}`,
      );
    } finally {
      setDeletingId(null);
    }
  };

  // ─── Render ────────────────────────────────────────────────────────────────
  return (
    <div className="h-full flex flex-col overflow-hidden bg-gradient-to-tr from-blue-50 via-indigo-50 to-teal-50 p-3 sm:p-6">
      <Header showSearch={false} showCart={false} />
      {toast && (
        <Toast
          type={toast.type}
          identifier={toast.identifier}
          message={toast.message}
          onClose={() => setToast(null)}
        />
      )}

      {/* History modal */}
      {historyTarget && (
        <HistoryModal
          stockId={historyTarget.stockId}
          productName={historyTarget.productName}
          onClose={() => setHistoryTarget(null)}
          showSize={!!fieldConfig?.size?.visible}
        />
      )}

      {showFullHistory && (
        <FullHistoryModal onClose={() => setShowFullHistory(false)} showSize={!!fieldConfig?.size?.visible} />
      )}

      {/* Search filters */}
      <section className="relative rounded-xl overflow-hidden shadow-md border border-blue-200 mt-4 flex-shrink-0">
        <h2 className="text-left text-sm font-bold text-blue-800 px-4 py-2 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200">
          🔍 Search Stock
        </h2>
        <div className="p-2">
          <div className="flex flex-wrap gap-2">
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Product Name
              </label>
              <input
                type="text"
                value={searchName}
                onChange={(e) => {
                  setSearchName(e.target.value);
                  saveSession({ searchName: e.target.value });
                }}
                onKeyDown={handleKeyDown}
                placeholder="e.g. Paracetamol"
                className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Manufacturer
              </label>
              <input
                type="text"
                value={searchMfr}
                onChange={(e) => {
                  setSearchMfr(e.target.value);
                  saveSession({ searchMfr: e.target.value });
                }}
                onKeyDown={handleKeyDown}
                placeholder="e.g. GSK"
                className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Bill Number
              </label>
              <input
                type="text"
                value={searchBillNo}
                onChange={(e) => {
                  setSearchBillNo(e.target.value);
                  saveSession({ searchBillNo: e.target.value });
                }}
                onKeyDown={handleKeyDown}
                placeholder="e.g. BILL-001"
                className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Distributor Name
              </label>
              <input
                type="text"
                value={searchDistName}
                onChange={(e) => {
                  setSearchDistName(e.target.value);
                  saveSession({ searchDistName: e.target.value });
                }}
                onKeyDown={handleKeyDown}
                placeholder="e.g. ABC Distributors"
                className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
              />
            </div>
            <div>
              <label className="block text-xs font-medium text-gray-600 mb-1">
                Distributor Mobile
              </label>
              <input
                type="text"
                value={searchDistMobile}
                onChange={(e) => {
                  const val = e.target.value.replace(/[^0-9]/g, "").slice(0, 10);
                  setSearchDistMobile(val);
                  saveSession({ searchDistMobile: val });
                }}
                onKeyDown={handleKeyDown}
                maxLength={10}
                placeholder="e.g. 9876543210"
                className="w-44 px-3 py-1 border border-blue-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-400 focus:outline-none"
              />
            </div>
          </div>
          <div className="flex justify-end gap-2 mt-3">
            <button
              onClick={() => {
                setSearchName("");
                setSearchMfr("");
                setSearchBillNo("");
                setSearchDistName("");
                setSearchDistMobile("");
                setClientFilter("");
                setBatchFilter("");
                setPage(0);
                sessionStorage.removeItem(SS);
              }}
              className="flex items-center gap-1.5 px-4 py-1.5 rounded-full border border-gray-300 text-gray-600 hover:bg-gray-50 font-semibold text-xs transition"
            >
              <i className="fas fa-redo" /> Reset
            </button>
            <button
              onClick={handleSearch}
              disabled={loading}
              className={`flex items-center justify-center gap-1.5 px-4 py-1.5 rounded-full text-white font-semibold shadow text-xs transition ${
                loading
                  ? "bg-gray-400 cursor-not-allowed"
                  : "bg-gradient-to-r from-blue-500 to-indigo-500 hover:from-indigo-600 hover:to-blue-600"
              }`}
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin" />
                  Searching...
                </>
              ) : (
                <>
                  <i className="fas fa-search" />
                  Search
                </>
              )}
            </button>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="relative mt-2 rounded-xl shadow-md border border-blue-200 flex flex-col flex-1 min-h-0">
        <div className="flex items-center justify-between px-5 py-2 bg-gradient-to-r from-indigo-100 via-blue-100 to-teal-100 border-b border-blue-200 flex-shrink-0">
          <h2 className="text-base font-bold text-blue-800">
            📋 Stock Details
            {results.length > 0 && (
              <span className="text-xs font-normal text-blue-600 ml-2">
                ({results.length} batch{results.length !== 1 ? "es" : ""})
              </span>
            )}
          </h2>
          <div className="flex items-center gap-2">
            <input
              type="text"
              value={clientFilter}
              onChange={(e) => { setClientFilter(e.target.value); setPage(0); }}
              placeholder="Filter by product..."
              className="px-2 py-1 border border-gray-300 rounded-lg text-xs w-36 focus:ring-1 focus:ring-indigo-400 focus:outline-none"
            />
            <input
              type="text"
              value={batchFilter}
              onChange={(e) => { setBatchFilter(e.target.value); setPage(0); }}
              placeholder="Filter by batch..."
              className="px-2 py-1 border border-gray-300 rounded-lg text-xs w-36 focus:ring-1 focus:ring-indigo-400 focus:outline-none"
            />
            {results.length > 0 && (
              <>
                <button
                  onClick={() => downloadPDF()}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-red-500 hover:bg-red-600 text-white text-xs font-semibold shadow transition"
                >
                  <i className="fas fa-file-pdf" /> PDF
                </button>
                <button
                  onClick={() => downloadExcel()}
                  className="flex items-center gap-1.5 px-3 py-1 rounded-full bg-green-600 hover:bg-green-700 text-white text-xs font-semibold shadow transition"
                >
                  <i className="fas fa-file-excel" /> Excel
                </button>
              </>
            )}
            <button
              onClick={() => setShowFullHistory(true)}
              title="View Stock History"
              className="text-indigo-500 hover:text-indigo-700 transition"
            >
              <i className="fas fa-history text-lg" />
            </button>
          </div>
        </div>
        <div className="overflow-auto flex-1">
          <table className="min-w-full text-sm text-gray-800 table-fixed">
            <colgroup>
              <col className="w-[140px]" />
              <col className="w-[110px]" />
              <col className="w-[120px]" />
              <col className="w-[100px]" />
              <col className="w-[70px]" />
              <col className="w-[100px]" />
              <col className="w-[90px]" />
              <col className="w-[70px]" />
              {fieldConfig?.expiryDate?.visible && <col className="w-[120px]" />}
              {fieldConfig?.size?.visible && <col className="w-[70px]" />}
                {fieldConfig?.boxNo?.visible && <col className="w-[80px]" />}
              <col className="w-[120px]" />
              <col className="w-[120px]" />
              <col className="w-[100px]" />
              {isAdmin && <col className="w-[100px]" />}
            </colgroup>
            <thead className="bg-gradient-to-r from-blue-100 via-indigo-50 to-teal-50 text-blue-900 text-xs uppercase tracking-wide sticky top-0 z-10 border-b border-blue-200">
              <tr>
                <Th
                  col="productName"
                  label="Product"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <Th
                  col="productType"
                  label="Product Type"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <Th
                  col="manufacturer"
                  label="Manufacturer"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <Th
                  col="batchNo"
                  label="Batch No"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                {fieldConfig?.boxNo?.visible && (
                  <Th
                    col="boxNo"
                    label="Box No"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                )}
                <Th
                  col="quantity"
                  label="Qty"
                  className="text-right"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <Th
                  col="purchasePrice"
                  label="Purchase ₹"
                  className="text-right"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <Th
                  col="mrp"
                  label="MRP ₹"
                  className="text-right"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <Th
                  col="salesDiscount"
                  label="Disc %"
                  className="text-right"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                {fieldConfig?.expiryDate?.visible && (
                  <Th
                    col="expiryDate"
                    label="Expiry Date"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                )}
                {fieldConfig?.size?.visible && (
                  <Th
                    col="size"
                    label="Size"
                    sortConfig={sortConfig}
                    onSort={handleSort}
                  />
                )}
                <Th
                  col="purchaseDate"
                  label="Purchase Date"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />

                <Th
                  col="distributorName"
                  label="Distributor"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                <Th
                  col="billNumber"
                  label="Bill No"
                  sortConfig={sortConfig}
                  onSort={handleSort}
                />
                {isAdmin && <th className="py-2 px-4 text-center">Actions</th>}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={totalCols} className="text-center py-6">
                    <i className="fas fa-spinner fa-spin text-blue-400 text-2xl" />
                  </td>
                </tr>
              ) : results.length === 0 ? (
                <tr>
                  <td
                    colSpan={totalCols}
                    className="py-10 text-center text-gray-400"
                  >
                    <i
                      className={`fas ${searched ? "fa-inbox" : "fa-search"} text-4xl mb-3 block`}
                    />
                    <p className="text-sm">
                      {searched
                        ? "No stock found. Try different search criteria."
                        : "Use the search above to find stock."}
                    </p>
                  </td>
                </tr>
              ) : (
                sortedResults
                  .slice(page * PAGE_SIZE, (page + 1) * PAGE_SIZE)
                  .map((r, idx) => {
                    const editing = !!editMap[r.stockId];
                    const ed = editMap[r.stockId] || {};
                    const expired = isExpired(r.expiryDate);
                    const expiringSoon =
                      !expired && isExpiringSoon(r.expiryDate);

                    return (
                      <tr
                        key={r.stockId}
                        className={`border-t transition-colors ${
                          expired
                            ? "bg-red-50"
                            : expiringSoon
                              ? "bg-yellow-50"
                              : idx % 2 === 0
                                ? "bg-white"
                                : "bg-blue-50"
                        } hover:bg-indigo-50`}
                      >
                        <td className="py-1 px-3 font-medium truncate">
                          <div className="truncate">{r.productName}</div>
                          {r.medicineType && (
                            <div className="text-xs text-gray-400 truncate">
                              {r.medicineType}
                            </div>
                          )}
                        </td>
                        <td className="py-1 px-3 truncate">
                          {r.medicineType || "—"}
                        </td>
                        
                        <td className="py-1 px-3 text-gray-600 truncate">
                          {r.manufacturer || "—"}
                        </td>

                        <td className="py-1 px-3">
                          <EditCell
                            editing={editing}
                            name="batchNo"
                            value={ed.batchNo}
                            onChange={(e) => handleEditChange(r.stockId, e)}
                            display={r.batchNo || "—"}
                            inputClassName="w-full min-w-[80px]"
                          />
                        </td>
                        {fieldConfig?.boxNo?.visible && (
                          <td className="py-1 px-3">
                            <EditCell
                              editing={editing}
                              name="boxNo"
                              value={ed.boxNo}
                              onChange={(e) => handleEditChange(r.stockId, e)}
                              display={r.boxNo || "—"}
                              inputClassName="w-full min-w-[60px]"
                            />
                          </td>
                        )}
                        <td className="py-1 px-3 text-right">
                          <EditCell
                            editing={editing}
                            type="number"
                            name="quantity"
                            value={ed.quantity}
                            onChange={(e) => handleEditChange(r.stockId, e)}
                            min="0"
                            display={
                              <span
                                className={
                                  r.quantity === 0
                                    ? "text-red-600 font-semibold"
                                    : ""
                                }
                              >
                                {r.quantity}
                              </span>
                            }
                            inputClassName="w-full min-w-[50px]"
                          />
                        </td>
                        <td className="py-1 px-3 text-right">
                          <EditCell
                            editing={editing}
                            type="number"
                            name="purchasePrice"
                            value={ed.purchasePrice}
                            onChange={(e) => handleEditChange(r.stockId, e)}
                            step="0.01"
                            min="0"
                            display={
                              r.purchasePrice ? `₹${r.purchasePrice}` : "—"
                            }
                            inputClassName="w-full min-w-[70px]"
                          />
                        </td>
                        <td className="py-1 px-3 text-right">
                          <EditCell
                            editing={editing}
                            type="number"
                            name="mrp"
                            value={ed.mrp}
                            onChange={(e) => handleEditChange(r.stockId, e)}
                            step="0.01"
                            min="0"
                            display={r.mrp ? `₹${r.mrp}` : "—"}
                            inputClassName="w-full min-w-[70px]"
                          />
                        </td>
                        <td className="py-1 px-3 text-right">
                          <EditCell
                            editing={editing}
                            type="number"
                            name="salesDiscount"
                            value={ed.salesDiscount}
                            onChange={(e) => handleEditChange(r.stockId, e)}
                            step="0.01"
                            min="0"
                            max="100"
                            display={
                              r.salesDiscount ? `${r.salesDiscount}%` : "0%"
                            }
                            inputClassName="w-full min-w-[50px]"
                          />
                        </td>
                        
                        {fieldConfig?.expiryDate?.visible && (
                          <td className="py-1 px-3">
                            {editing ? (
                              <input
                                type="date"
                                name="expiryDate"
                                value={ed.expiryDate}
                                onChange={(e) => handleEditChange(r.stockId, e)}
                                className="w-full min-w-[110px] px-2 py-1 border border-indigo-300 rounded text-sm focus:ring-1 focus:ring-indigo-400 focus:outline-none"
                              />
                            ) : (
                              <span
                                className={
                                  expired
                                    ? "text-red-600 font-semibold"
                                    : expiringSoon
                                      ? "text-yellow-600 font-semibold"
                                      : ""
                                }
                              >
                                {fmtDate(r.expiryDate)}
                                {expired && (
                                  <span className="ml-1 text-xs bg-red-100 text-red-600 px-1 rounded">
                                    Expired
                                  </span>
                                )}
                                {expiringSoon && (
                                  <span className="ml-1 text-xs bg-yellow-100 text-yellow-600 px-1 rounded">
                                    Soon
                                  </span>
                                )}
                              </span>
                            )}
                          </td>
                        )}
                        {fieldConfig?.size?.visible && (
                        <td className="py-1 px-3 text-right">
                          <EditCell
                            editing={editing}
                            type="number"
                            name="size"
                            value={ed.size}
                            onChange={(e) => handleEditChange(r.stockId, e)}
                            step="0.01"
                            min="0"
                            max="100"
                            display={
                              r.size ? `${r.size}` : "0"
                            }
                            inputClassName="w-full min-w-[50px]"
                          />
                        </td>)}
                        
                        <td className="py-1 px-3">
                          <EditCell
                            editing={editing}
                            type="date"
                            name="purchaseDate"
                            value={ed.purchaseDate}
                            onChange={(e) => handleEditChange(r.stockId, e)}
                            display={fmtDate(r.purchaseDate)}
                            inputClassName="w-full min-w-[110px]"
                          />
                        </td>
                        <td className="py-1 px-3 text-gray-600 truncate">
                          {r.distributorInfo?.distributorName || "—"}
                        </td>
                        <td className="py-1 px-3 text-gray-600 truncate">
                          {r.distributorInfo?.billNumber || "—"}
                        </td>
                        {isAdmin && (
                          <td className="py-1 px-3">
                            <div className="flex items-center justify-center gap-2">
                              {editing ? (
                                <>
                                  <button
                                    onClick={() => handleSave(r.stockId)}
                                    disabled={savingId === r.stockId}
                                    title="Save"
                                    className="text-green-600 hover:text-green-800 disabled:opacity-50"
                                  >
                                    {savingId === r.stockId ? (
                                      <i className="fas fa-spinner fa-spin" />
                                    ) : (
                                      <i className="fas fa-check" />
                                    )}
                                  </button>
                                  <button
                                    onClick={() => cancelEdit(r.stockId)}
                                    title="Cancel"
                                    className="text-gray-500 hover:text-gray-700"
                                  >
                                    <i className="fas fa-times" />
                                  </button>
                                </>
                              ) : (
                                <>
                                  <button
                                    onClick={() => startEdit(r)}
                                    title="Edit"
                                    className="text-blue-500 hover:text-blue-700"
                                  >
                                    <i className="fas fa-edit" />
                                  </button>
                                  <button
                                    onClick={() =>
                                      setHistoryTarget({
                                        stockId: r.stockId,
                                        productName: r.productName,
                                      })
                                    }
                                    title="View History"
                                    className="text-indigo-500 hover:text-indigo-700"
                                  >
                                    <i className="fas fa-history" />
                                  </button>
                                  <button
                                    onClick={() =>
                                      handleDelete(r.stockId, r.productName)
                                    }
                                    disabled={deletingId === r.stockId}
                                    title="Delete"
                                    className="text-red-500 hover:text-red-700 disabled:opacity-50"
                                  >
                                    {deletingId === r.stockId ? (
                                      <i className="fas fa-spinner fa-spin" />
                                    ) : (
                                      <i className="fas fa-trash-alt" />
                                    )}
                                  </button>
                                </>
                              )}
                            </div>
                          </td>
                        )}
                      </tr>
                    );
                  })
              )}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        {results.length > 0 && (
          <div className="flex items-center justify-between px-5 py-2 border-t border-gray-100 bg-white flex-shrink-0">
            <span className="text-xs text-gray-500">
              Showing {Math.min(page * PAGE_SIZE + 1, filteredResults.length)}–
              {Math.min((page + 1) * PAGE_SIZE, filteredResults.length)} of{" "}
              {filteredResults.length}
            </span>
            <div className="flex items-center gap-1">
              <button
                onClick={() => setPage(0)}
                disabled={page === 0}
                className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                «
              </button>
              <button
                onClick={() => setPage(page - 1)}
                disabled={page === 0}
                className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                ‹
              </button>
              {(() => {
                const totalPages = Math.ceil(filteredResults.length / PAGE_SIZE);
                const start = Math.max(0, Math.min(page - 2, totalPages - 5));
                const end = Math.min(totalPages, start + 5);
                return Array.from({ length: end - start }, (_, i) => start + i).map((i) => (
                  <button
                    key={i}
                    onClick={() => setPage(i)}
                    className={`px-2 py-1 text-xs rounded border ${
                      i === page
                        ? "bg-indigo-500 text-white border-indigo-500"
                        : "border-gray-200 hover:bg-gray-50"
                    }`}
                  >
                    {i + 1}
                  </button>
                ));
              })()}
              <button
                onClick={() => setPage(page + 1)}
                disabled={page >= Math.ceil(filteredResults.length / PAGE_SIZE) - 1}
                className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                ›
              </button>
              <button
                onClick={() =>
                  setPage(Math.ceil(filteredResults.length / PAGE_SIZE) - 1)
                }
                disabled={page >= Math.ceil(filteredResults.length / PAGE_SIZE) - 1}
                className="px-2 py-1 text-xs rounded border border-gray-200 disabled:opacity-40 hover:bg-gray-50"
              >
                »
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
};

export default StockSearch;
